import { useEffect, type ReactNode } from "react";

/**
 * A form that slides up from the bottom on a phone and sits centred on a
 * wider screen. Pages open these instead of keeping add forms mounted at rest:
 * the approved redesign (2026-09-10) is lists first, forms on demand.
 *
 * Closes on Escape and on a tap outside the panel, never on a tap inside it.
 */
export function Sheet({
  title,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  onClose: () => void;
  /** Room for a card's detail tables, which a form does not need. */
  wide?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Scrolls inside itself: a card's four tabs can outgrow the screen,
        // and the page behind a modal must not be what scrolls.
        className={`max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl bg-neutral-900 p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:rounded-2xl ${
          wide ? "max-w-2xl" : "max-w-lg"
        }`}
      >
        <div className="mb-4 flex items-center">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto grid h-10 w-10 place-items-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
